/**
 * Role helpers for the browser.
 *
 * The server sends `effectiveRole` on the profile. It is the only role field
 * worth reading: the stored `role` is just 'customer' or 'staff', while super
 * user and IT come from environment lists the browser cannot see, and a staff
 * member whose approval was withdrawn still carries role 'staff' on their row.
 *
 * These are convenience checks for showing and hiding UI. They are not
 * security — every one of them is re-checked on the server, because anything
 * decided in a browser can be edited in a browser.
 */

export type EffectiveRole = "super_user" | "it" | "staff" | "customer";

export type ModuleKey =
    | "dashboard" | "staff" | "groups" | "branches" | "approvals"
    | "limits" | "bookings" | "passengers" | "markup" | "content" | "reports" | "settings";

export interface RoleBearing {
    effectiveRole?: string | null;
    /** Stored role. Present on older payloads; prefer effectiveRole. */
    role?: string | null;
    /**
     * What permission groups grant this person, resolved by the server.
     *
     * Super users and IT arrive holding everything, so nothing here needs to
     * special-case them.
     */
    permissions?: Partial<Record<ModuleKey, { view?: boolean; manage?: boolean }>> | null;
}

function effective(profile?: RoleBearing | null): EffectiveRole {
    const role = profile?.effectiveRole;
    if (role === "super_user" || role === "it" || role === "staff" || role === "customer") {
        return role;
    }
    return "customer";
}

/** Staff, super user or IT — anyone who may use the staff-facing product. */
export function isStaff(profile?: RoleBearing | null): boolean {
    const role = effective(profile);
    return role === "staff" || role === "super_user" || role === "it";
}

/** Super user or IT — the dashboard, approvals, limits, branches. */
export function isSuperUser(profile?: RoleBearing | null): boolean {
    const role = effective(profile);
    return role === "super_user" || role === "it";
}

export function getEffectiveRole(profile?: RoleBearing | null): EffectiveRole {
    return effective(profile);
}

/**
 * May this person use a module?
 *
 * `manage` implies `view`, matching the server. Like every helper here this
 * only decides what to draw — the same question is asked again on the server,
 * because anything decided in a browser can be edited in a browser.
 */
export function can(
    profile: RoleBearing | null | undefined,
    module: ModuleKey,
    action: "view" | "manage" = "view",
): boolean {
    const grant = profile?.permissions?.[module];
    if (!grant) return false;
    return action === "manage" ? grant.manage === true : grant.view === true || grant.manage === true;
}
